/* eslint-disable i18next/no-literal-string */
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AppBar,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { faGlobe, faRightFromBracket } from "@fortawesome/pro-light-svg-icons";
import { faSitemap, faUser } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";

import { SettingPopover } from "@utilifeed/components";
import { generateAuthState } from "@utilifeed/components/propai";
import * as CONSTANTS from "@utilifeed/components/ui/layout/constants";
import { PLAYWRIGHT_RUN } from "@utilifeed/config";
import { InteractionStore, logger, useBrandingContext } from "@core";
import { useDataApi } from "@core/apis";
import { PropAIApi } from "@core/apis/PropAI";
import {
  getBrandingQueryOptions,
  getPropAIOrganizationListQueryOptions,
} from "@core/apis/PropAI/queries";
import { getSubstationByOrganizationQueryOptions } from "@core/apis/shared";
import { FontAwesomeSvgIcon, LoaderScreen } from "@components";

import { LanguageSelect } from "../lib/components/ui/LanguageSelection";
import i18n from "../lib/propai-i18n";
import languageStore from "../lib/stores/language.store";
import organizationStore from "../lib/stores/organization.store";

export const pageTitle = "Optivarme";

const AppLayout = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dataApi = useDataApi();
  const { branding } = useBrandingContext();

  const appLogo = branding.logos.light;
  const appTitle = branding.text.appTitle;
  const companyName = branding.text.companyName;

  const stubOrganization = {
    uid: "org-1",
    name: "Demo Organization",
    external_id: "org-1",
    is_admin: false,
  };
  const organizationQuery = useQuery({
    ...getPropAIOrganizationListQueryOptions(dataApi as PropAIApi | null),
    enabled: !!dataApi && !PLAYWRIGHT_RUN,
  });

  const organizationList = PLAYWRIGHT_RUN ? [stubOrganization] : organizationQuery.data;
  const organizationLoading = PLAYWRIGHT_RUN ? false : organizationQuery.isLoading;
  const organizationLoadedSuccess = PLAYWRIGHT_RUN ? true : organizationQuery.isSuccess;
  const organizationError = PLAYWRIGHT_RUN ? false : organizationQuery.isError;

  const {
    currentLanguage,
    selectedLanguage,
    languageOptions,
    updateLanguage,
    confirmLanguage,
    resetLanguage,
  } = languageStore();

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    (async () => {
      await i18n.changeLanguage(currentLanguage);
    })();
  }, [currentLanguage]);

  const handleConfirmLanguage = () => {
    (async () => {
      confirmLanguage();
      await i18n.changeLanguage(languageStore.getState().currentLanguage);
      handleClose();
    })();
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleCancel = () => {
    resetLanguage();
    handleClose();
  };

  const handleClose = () => {
    setPopoverAnchorEl(null);
  };

  const { auth, queryClient } = Route.useRouteContext();

  const selectedOrganization = organizationStore((state) => state.selectedOrganization);
  const setOrganization = organizationStore((state) => state.setOrganization);
  const navigate = useNavigate();

  const isAdmin = selectedOrganization?.is_admin ?? false;

  // handle organization change and check for single substation
  useEffect(() => {
    if (PLAYWRIGHT_RUN) return;
    if (!selectedOrganization?.uid) return;

    // Check fi organization already processed
    const processedOrgs = JSON.parse(sessionStorage.getItem("processedOrgs") || "{}");
    if (processedOrgs[selectedOrganization.uid]) return;

    // Get substations for this organization
    const checkForSingleSubstation = async () => {
      try {
        const queryOptions = getSubstationByOrganizationQueryOptions(
          dataApi,
          selectedOrganization.uid
        );
        const substations = await queryClient.ensureQueryData(queryOptions);

        if (substations && substations.length === 1) {
          const substationId = substations[0].uid;

          // Mark organization as processed
          processedOrgs[selectedOrganization.uid] = true;
          sessionStorage.setItem("processedOrgs", JSON.stringify(processedOrgs));

          void navigate({
            to: "/$organizationId/properties/$substationId/overview",
            params: {
              organizationId: selectedOrganization.uid,
              substationId: substationId,
            },
            replace: true,
          });
        } else {
          processedOrgs[selectedOrganization.uid] = true;
          sessionStorage.setItem("processedOrgs", JSON.stringify(processedOrgs));
        }
      } catch (error) {
        logger.error("Error checking for single substation", { error });
      }
    };

    void checkForSingleSubstation();
  }, [selectedOrganization, navigate, queryClient]);

  useEffect(() => {
    if (!organizationLoadedSuccess || !dataApi) return;
    if (selectedOrganization) return;
    if (organizationList?.length) {
      const organization = organizationList[0]; // set first organization as default
      if (organization) {
        organizationStore.setState({ selectedOrganization: organization });
        // Branding is normally resolved in beforeLoad, but this effect handles the
        // fallback case where beforeLoad exited early (e.g. API not yet initialized).
        void queryClient
          .ensureQueryData(getBrandingQueryOptions(dataApi as PropAIApi | null, organization.uid))
          .then((branding) => {
            organizationStore.setState({ branding: branding ?? null });
            return branding;
          });
      }
    }
  }, [organizationLoadedSuccess, organizationList, selectedOrganization, dataApi, queryClient]);

  const handleOrganizationChange = async (event: SelectChangeEvent<string>) => {
    const selectedOrg = event.target.value;
    const foundOrg = organizationList?.find((org) => org.uid === selectedOrg);
    if (foundOrg) {
      setOrganization(foundOrg);
      // Fetch branding for the newly selected organization.
      // ensureQueryData returns cached data when available.
      const branding = await queryClient.ensureQueryData(
        getBrandingQueryOptions(dataApi as PropAIApi | null, foundOrg.uid)
      );
      organizationStore.setState({ branding: branding ?? null });
    }
  };

  const outletRenderer = organizationLoadedSuccess ? <Outlet /> : <LoaderScreen />;

  logger.debug("RENDER <AppLayout> %j", { selectedOrganization });

  return (
    <>
      <AppBar
        elevation={0}
        position="static"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: "primary.dark",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          color: "white",
          height: "64px",
        }}
      >
        <Toolbar>
          <Grid container display={"flex"} justifyContent="space-between" alignItems="center">
            <Grid item sx={isAdmin ? { display: "flex", flexDirection: "row", gap: 2 } : undefined}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FontAwesomeIcon icon={faUser} size="lg" />
                <Typography variant="titleMedium" display="inline" pl={1} sx={{ color: "white" }}>
                  {selectedOrganization?.name}
                </Typography>
              </Box>
              {isAdmin && (
                <Link
                  to="/$organizationId/admin"
                  params={{ organizationId: selectedOrganization?.uid || "" }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      color: "white",
                      borderColor: "white",
                      borderRadius: "24px",
                      textTransform: "none",
                    }}
                  >
                    {t("action_manage_organization", { ns: "_action" })}
                  </Button>
                </Link>
              )}
            </Grid>

            <Grid
              item
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box display="flex" justifyContent="center" alignItems="center">
                <Link to="/app" style={{ display: "flex", border: "none", boxShadow: "none" }}>
                  <img
                    src={appLogo}
                    alt={`${companyName} | ${appTitle}`}
                    height="24px"
                    style={{ objectFit: "contain", border: "none", boxShadow: "none" }}
                  />
                </Link>
                <Typography
                  variant="h4"
                  data-testid="page-title"
                  fontSize={24}
                  fontWeight={400}
                  ml={2}
                >
                  {appTitle}
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <Box display="flex" alignItems="center">
                  <FontAwesomeIcon icon={faSitemap} />
                  {organizationLoading ? (
                    <Typography variant="body1" p={2}>
                      Loading...
                    </Typography>
                  ) : (
                    <Box display="flex" alignItems="center">
                      {selectedOrganization ? (
                        <Select
                          labelId="organization-select-label"
                          value={selectedOrganization?.uid}
                          onChange={(val) => handleOrganizationChange(val)}
                          sx={{
                            color: "white",
                            ...theme.typography.labelLarge,
                            svg: { fill: "white" },
                            ".MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent",
                            },
                          }}
                        >
                          {organizationList?.map((org) => (
                            <MenuItem key={org.uid} value={org.uid}>
                              {org.name}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : null}
                    </Box>
                  )}
                </Box>

                {/* Language selection */}
                <IconButton sx={{ columnGap: 1 }} size="small" onClick={handleClick}>
                  <Typography
                    variant="labelLarge"
                    color="white"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {currentLanguage}
                  </Typography>
                  <ReactCountryFlag
                    svg
                    countryCode={CONSTANTS.flagMap[currentLanguage] || currentLanguage}
                    style={{
                      fontSize: "1.5em",
                      lineHeight: "1.5em",
                    }}
                  />
                </IconButton>

                {/* Logout */}
                <Box display="flex" justifyContent="flex-end" alignItems="center">
                  <Tooltip title="Logout">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        const result = await InteractionStore.store
                          .getState()
                          .confirm(
                            t("action_logout", { ns: "_action" }),
                            t("text_logout_confirmation", { ns: "_common" }),
                            {
                              confirmText: t("action_logout", { ns: "_action" }),
                              cancelText: t("action_cancel", { ns: "_action" }),
                            }
                          );

                        if (result === "confirmed") {
                          auth?.logout({ logoutParams: { returnTo: window.location.origin } });
                        }
                      }}
                    >
                      <FontAwesomeIcon color="white" icon={faRightFromBracket} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Language option renderer */}
      <SettingPopover
        id="language-option"
        open={!!popoverAnchorEl}
        anchorEl={popoverAnchorEl}
        handleClose={handleClose}
      >
        <Box display="flex" flexDirection="column" width="100%" px={2} pb={2} pt={3} gap={2}>
          <Box display={"inherit"} alignItems={"center"} gap={1}>
            <FontAwesomeSvgIcon icon={faGlobe} sx={{ width: "20px", height: "20px" }} />
            <Typography variant="caption">{t("text_language", { ns: "_common" })}</Typography>
          </Box>
          <LanguageSelect
            value={selectedLanguage}
            options={languageOptions}
            onChange={updateLanguage}
            width={CONSTANTS.DROPDOWN_WIDTH}
          />
        </Box>
        <Box px={2} pb={3} pt={2} display="flex" justifyContent="space-between">
          <Button
            sx={{ width: "140px", height: "30px" }}
            variant="contained"
            color="primary"
            onClick={handleConfirmLanguage}
          >
            {t("action_confirm", { ns: "_action" })}
          </Button>
          <Button
            sx={{ width: "140px", height: "30px" }}
            variant="outlined"
            color="primary"
            onClick={handleCancel}
          >
            {t("action_cancel", { ns: "_action" })}
          </Button>
        </Box>
      </SettingPopover>

      {/* Main/Route Content */}
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: `calc(100% - 5.14rem)`,
          color: "grey.800",
        }}
      >
        {/* Body */}
        <Box
          component="section"
          height="100%"
          overflow="auto"
          flexGrow={1}
          display="flex"
          flexDirection="column"
        >
          {organizationError ? (
            <Box p={4}>
              <Alert severity="error">
                This likely means you dont have access to any organization. Please contact your
                admin.
              </Alert>
            </Box>
          ) : (
            outletRenderer
          )}
        </Box>
      </Box>
    </>
  );
};

export const Route = createFileRoute("/app")({
  component: AppLayout,
  beforeLoad: async ({ context, location }) => {
    if (!context.auth) return;

    if (!context.auth?.isAuthenticated && context.auth?.isLoading === false) {
      // Generate and store state before redirect
      const state = generateAuthState(location.href);

      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
          state,
        },
      });
    }

    if (!context.apis?.propAiApi) return;

    // Resolve org and branding before any route renders.
    // Prevents default-theme flicker on direct navigation to child routes.
    const orgs = await context.queryClient.ensureQueryData(
      getPropAIOrganizationListQueryOptions(context.apis.propAiApi as PropAIApi)
    );

    if (orgs?.length) {
      const selectedOrg = organizationStore.getState().selectedOrganization || orgs[0];
      if (selectedOrg?.uid) {
        // Fetch branding
        const branding = await context.queryClient.ensureQueryData(
          getBrandingQueryOptions(context.apis.propAiApi as PropAIApi, selectedOrg.uid)
        );
        organizationStore.setState({
          selectedOrganization: selectedOrg,
          branding: branding ?? null,
        });
      }
    }

    // Substation redirect logic only applies to the /app index page
    if (location.pathname !== "/app") return;
    if (location.search && (location.search as Record<string, string>).skipRedirect === "true")
      return;

    if (!orgs || orgs.length === 0) return;

    const selectedOrg = organizationStore.getState().selectedOrganization || orgs[0];
    if (!selectedOrg?.uid) return;

    // Allow us to come back to the main page after redirect
    const redirectedOrgs = JSON.parse(sessionStorage.getItem("redirectedOrgs") || "{}");
    if (redirectedOrgs[selectedOrg.uid]) return;

    const substations = await context.queryClient.ensureQueryData(
      getSubstationByOrganizationQueryOptions(context.apis?.propAiApi, selectedOrg.uid)
    );

    // Redirect to its indoor-climate page for first time if only one substation
    if (substations && substations.length === 1) {
      const substationId = substations[0].uid;

      // Mark organization
      redirectedOrgs[selectedOrg.uid] = true;
      sessionStorage.setItem("redirectedOrgs", JSON.stringify(redirectedOrgs));

      throw redirect({
        to: "/$organizationId/properties/$substationId/indoor-climate",
        params: {
          organizationId: selectedOrg.uid,
          substationId: substationId,
        },
        replace: true,
      });
    }
  },
});
